import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import { Plus, Building2, MapPin, Users, Edit2, Trash2, X, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const ASSET_TYPES = ["facility", "vehicle", "equipment", "process"];
const SECTORS = ["Agriculture", "Construction", "Energy", "Manufacturing", "Mining", "Retail & FMCG", "Technology", "Transport & Logistics", "Waste Management & Recycling", "Water & Utilities", "Other"];

export default function Assets() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [form, setForm] = useState({ name: "", type: "facility", description: "", location: "", sector: "", country: "", employee_count: "" });

  useEffect(() => { loadAssets(); }, [currentOrg]);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const q = currentOrg ? `?org_id=${currentOrg.id}` : "";
      const res = await client.get(`/assets${q}`);
      setAssets(res.data);
    } catch { toast.error("Failed to load assets"); }
    setLoading(false);
  };

  const openCreate = () => {
    setEditingAsset(null);
    setForm({ name: "", type: "facility", description: "", location: "", sector: "", country: "", employee_count: "" });
    setShowModal(true);
  };

  const openEdit = (asset) => {
    setEditingAsset(asset);
    setForm({ name: asset.name, type: asset.type, description: asset.description || "", location: asset.location || "", sector: asset.sector || "", country: asset.country || "", employee_count: asset.employee_count || "" });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await client.put(`/assets/${editingAsset.id}`, form);
        toast.success("Asset updated");
      } else {
        await client.post("/assets", { ...form, org_id: currentOrg?.id, employee_count: form.employee_count ? parseInt(form.employee_count) : null });
        toast.success("Asset created");
      }
      setShowModal(false);
      loadAssets();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save asset"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try { await client.delete(`/assets/${id}`); toast.success("Asset deleted"); loadAssets(); }
    catch { toast.error("Failed to delete asset"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
          
      <PageHeader
        title="Assets"
        subtitle="Facilities, vehicle fleets, and operations you track emissions for."
      />

      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Total Assets" value={assets.length} color="text-slate-900" />
      </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] transition-colors" data-testid="add-asset-btn">
          <Plus size={14} /> Add Asset
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-white border border-slate-200 rounded-xl animate-pulse" />)}
        </div>
      ) : assets.length === 0 ? (
        <SectionCard>
          <Building2 size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-base font-medium text-slate-600">No assets yet</p>
          <p className="text-sm text-slate-400 mt-1">Add your first facility, vehicle fleet, or equipment to begin tracking emissions.</p>
          <button onClick={openCreate} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B]">
            <Plus size={14} /> Add First Asset
          </button>
        </SectionCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assets-grid">
          {assets.map(asset => (
            <div key={asset.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-all duration-200 group" data-testid={`asset-card-${asset.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Building2 size={16} className="text-slate-500" />
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(asset)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" data-testid={`edit-asset-${asset.id}`}><Edit2 size={13} /></button>
                  <button onClick={() => handleDelete(asset.id)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500" data-testid={`delete-asset-${asset.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">{asset.name}</h3>
              <div className="flex items-center gap-1 text-xs text-slate-400 mb-1">
                <span className="capitalize">{asset.type}</span>
                {asset.sector && <><span>·</span><span>{asset.sector}</span></>}
              </div>
              {asset.location && (
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <MapPin size={11} /> {asset.location}
                </div>
              )}
              {asset.description && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{asset.description}</p>}
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">{asset.status || "active"}</span>
                <button onClick={() => navigate(`/assessments?asset_id=${asset.id}`)} className="text-xs text-[#059669] hover:underline flex items-center gap-1">
                  View assessments <ChevronRight size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAsset ? "Edit Asset" : "Add Asset"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Asset Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" required data-testid="asset-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:ring-2 focus:ring-[#059669]">
                  {ASSET_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Sector</label>
                <select value={form.sector} onChange={e => setForm(f => ({ ...f, sector: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:ring-2 focus:ring-[#059669]">
                  <option value="">Select sector</option>
                  {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Location</label>
              <input type="text" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" placeholder="City, Country" data-testid="asset-location-input" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669] resize-none h-16" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 h-10 border border-slate-300 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50">Cancel</button>
              <button type="submit" className="flex-1 h-10 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B]" data-testid="save-asset-btn">Save Asset</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
